export const PLAYTECH_API_EVENTS = {

    TWO_FA_POPUP_OPENED: '2fa-popup-opened',
    TWO_FA_POPUP_CLOSED: '2fa-popup-closed',
    // This event is described in the file useOnCookieSettingsUpdated.js
    COOKIE_REGULATION: 'cookie-settings-updated',
    // This event is described in the file useOnLangChange.ts
    LANGUAGE_CHANGED: 'user:action:changeLanguage',
    /**
     * Event fires when user successfully login with credentials on the portal. Event does not trigger after portal reload.
     * @memberOf Playtech.API.events
     * @event LOGGED_IN
     * @type {callback}
     * @example Playtech.on('LOGGED_IN', () => { ... });
     */
    LOGGED_IN: 'auth:credentials:login',
    /**
     * The event fires when the user fails to login with credentials on the portal.
     * @memberOf Playtech.API.events
     * @event LOGGED_IN_FAIL
     * @type {callback}
     * @param {string[]} error Login error.
     * @example Playtech.on('LOGGED_IN_FAIL', ([error]) => { ... });
     */
    LOGGED_IN_FAIL: 'auth:credentials:login:failed',
    /**
     * The event fires when the user logout on the portal.
     * @memberOf Playtech.API.events
     * @event LOGGED_OUT
     * @type {callback}
     * @example Playtech.on('LOGGED_OUT', () => { ... });
     */
    LOGGED_OUT: 'auth:logout',
    /**
     * Event fires on successful registration.
     * @memberOf Playtech.API.events
     * @event REGISTRATION_COMPLETED
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].username
     * @param {string} details[].userId
     * @param {number} details[].playerCode
     * @example Playtech.on('REGISTRATION_COMPLETED', ([{ username, userId, playerCode }]) => { ... });
     */
    REGISTRATION_COMPLETED: 'registration:success',
    /**
     * The event fires when an error occurs during registration.
     * @memberOf Playtech.API.events
     * @event REGISTRATION_FAIL
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].content
     * @example Playtech.on('REGISTRATION_FAIL', ([{ content }]) => { ... });
     */
    REGISTRATION_FAIL: 'registration:error',
    /**
     * The event fires when the registration step changes.
     * @memberOf Playtech.API.events
     * @event REGISTRATION_STEP_CHANGED
     * @type {callback}
     * @param {Array<string | number>} details
     * @param {(string|number)} details[].nextStep
     * @param {(string|number)} details[].currentStep
     * @example Playtech.on('REGISTRATION_STEP_CHANGED', ([nextStep, currentStep]) => { ... });
     */
    REGISTRATION_STEP_CHANGED: 'registration:changeStep',
    /**
     * The event is fired when registration form is submitted.
     * @memberOf Playtech.API.events
     * @event REGISTRATION_SUBMITTED
     * @type {callback}
     * @example Playtech.on('REGISTRATION_SUBMITTED', () => { ... });
     */
    REGISTRATION_SUBMITTED: 'registration:submitted',
    /**
     * The event fires when the deposit has been successfully completed.
     * @memberOf Playtech.API.events
     * @event DEPOSIT_COMPLETED
     * @type {callback}
     * @param {Object[]} details { amount: 1, code: '40564463', method: 'Retail Deposit', status: 'waiting' }
     * @param {number} details[].amount
     * @param {string} details[].code
     * @param {string} details[].method
     * @param {string} details[].status
     * @example Playtech.on('DEPOSIT_COMPLETED', ([{ amount, code, method, status }]) => { ... });
     */
    DEPOSIT_COMPLETED: 'deposit:success',
    /**
     * The event is fired when the deposit was not completed successfully.
     * @memberOf Playtech.API.events
     * @event DEPOSIT_FAIL
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].correlationId
     * @param {Object} details[].error
     * @param {number} details[].errorCode
     * @param {string} details[].errorDescription
     * @param {string} details[].errorMessage
     * @example Playtech.on('DEPOSIT_FAIL', ([{ correlationId, error, errorCode, errorDescription, errorMessage }]) => { ... });
     */
    DEPOSIT_FAIL: 'deposit:fail',
    /**
     * The event fires when the withdrawal has been successfully completed.
     * @memberOf Playtech.API.events
     * @event WITHDRAWAL_COMPLETED
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].amount
     * @param {string} details[].method
     * @param {string} details[].status
     * @param {string} details[].transactionCode
     * @example Playtech.on('WITHDRAWAL_COMPLETED', ([{ amount, method, status, transactionCode }]) => { ... });
     */
    WITHDRAWAL_COMPLETED: 'withdraw:success',
    /**
     * The event is fired when the withdraw was not completed successfully.
     * @memberOf Playtech.API.events
     * @type {callback}
     * @event WITHDRAWAL_FAIL
     * @param {Object[]} details
     * @param {string} details[].correlationId
     * @param {Object} details[].error
     * @param {number} details[].errorCode
     * @param {string} details[].errorDescription
     * @param {string} details[].errorMessage
     * @example Playtech.on('WITHDRAWAL_FAIL', ([{ correlationId, error, errorCode, errorDescription, errorMessage }]) => { ... });
     */
    WITHDRAWAL_FAIL: 'withdraw:fail',
    FORGOT_EMAIL_SUCCESS: 'forgotEmail:success',
    FORGOT_EMAIL_FAIL: 'forgotEmail:fail',
    FORGOT_USERNAME_SUCCESS: 'forgotUsername:success',
    FORGOT_USERNAME_FAIL: 'forgotUsername:fail',
    FORGOT_PASSWORD_SUCCESS: 'forgotPassword:success',
    FORGOT_PASSWORD_FAIL: 'forgotPassword:fail',
    /**
     * The event is fired after action "analytics" is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_ANALYTICS
     * @type {callback}
     * @param {Object} details
     * @example Playtech.on('SAFECHARGE_ANALYTICS', (data) => { ... });
     */
    SAFECHARGE_ANALYTICS: 'safecharge:analytics',
    /**
     * The event is fired after payment method registration failed action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_PM_REGISTRATION_FAIL
     * @type {callback}
     * @param {Object} details
     * @example Playtech.on('SAFECHARGE_PM_REGISTRATION_FAIL', (data) => { ... });
     */
    SAFECHARGE_PM_REGISTRATION_FAIL: 'safecharge:failedRegistration',
    /**
     * The event is fired after payment method successful registration action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_PM_REGISTRATION_SUCCESS
     * @type {callback}
     * @param {Object} details
     * @example Playtech.on('SAFECHARGE_PM_REGISTRATION_SUCCESS', (data) => { ... });
     */
    SAFECHARGE_PM_REGISTRATION_SUCCESS: 'safecharge:successfulRegistration',
    /**
     * The event is fired after contact support action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_CONTACT_SUPPORT
     * @type {callback}
     * @param {Object} details
     * @example Playtech.on('SAFECHARGE_CONTACT_SUPPORT', (data) => { ... });
     */
    SAFECHARGE_CONTACT_SUPPORT: 'safecharge:contactSupport',
    /**
     * The event is fired after alternative verification action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_ALTERNATIVE_VERIFICATION
     * @type {callback}
     * @param {Object} details
      * @example Playtech.on('SAFECHARGE_ALTERNATIVE_VERIFICATION', (data) => { ... });
     */
    SAFECHARGE_ALTERNATIVE_VERIFICATION: 'safecharge:alternativeVerification',
    /**
     * The event is fired after successful withdraw action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_WITHDRAWAL_SUCCESS
     * @type {callback}
     * @param {Object} details
     * @example Playtech.on('SAFECHARGE_WITHDRAWAL_SUCCESS', (data) => { ... });
     */
    SAFECHARGE_WITHDRAWAL_SUCCESS: 'safecharge:successfulWithdrawalRequest',
    /**
     * The event is fired after withdraw failed action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_WITHDRAWAL_FAIL
     * @type {callback}
     * @param {Object} details
     * @example Playtech.on('SAFECHARGE_WITHDRAWAL_FAIL', (data) => { ... });
     */
    SAFECHARGE_WITHDRAWAL_FAIL: 'safecharge:failedWithdrawalRequest',
    /**
     * The event is fired when the web content was rendered.
     * @memberOf Playtech.API.events
     * @event WEB_CONTENT_RENDERED
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].wc
     * @example Playtech.on('WEB_CONTENT_RENDERED', ([wc]) => { ... });
     */
    WEB_CONTENT_RENDERED: 'webContent:rendered',
    /**
     * The event is fired when games portlet is mounted.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_DISPLAY
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].container
     * @param {string} details[].gameFeed
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_DISPLAY', ([{ container, gameFeed }]) => { ... });
     */
    RESPONSIVE_GAME_PORTLET_DISPLAY: 'games:mount',
    /**
     * The event is fired when game portlet category was switched.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_CATEGORY_SWITCH
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].container
     * @param {string} details[].gameFeed
     * @param {string} details[].categoryName
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_CATEGORY_SWITCH', ([{ container, gameFeed, categoryName }]) => { ... });
     */
    RESPONSIVE_GAME_PORTLET_CATEGORY_SWITCH: 'game:category:switch',
    /**
     * The event is fired when games category is expanded.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_CATEGORY_EXPAND
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].container
     * @param {string} details[].gameFeed
     * @param {string} details[].categoryName
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_CATEGORY_EXPAND', ([{ container, gameFeed, categoryName }]) => { ... });
     */
    RESPONSIVE_GAME_PORTLET_CATEGORY_EXPAND: 'game:category:expand',
    /**
     * The event is fired when active slide was changed.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_CATEGORY_SCROLL
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].container
     * @param {string} details[].gameFeed
     * @param {string} details[].categoryName
     * @param {string} details[].elementClicked
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_CATEGORY_SCROLL', ([{ container, gameFeed, categoryName, elementClicked }]) => { ... });
     */
    RESPONSIVE_GAME_PORTLET_CATEGORY_SCROLL: 'game:category:activeSlide:change',
    /**
     * The event is fired when personalized game grid category is configured.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_PERSONALIZED_GRID_2
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].container
     * @param {string} details[].gameFeed
     * @param {string} details[].personalizedCategoryName
     * @param {number} details[].personalizedGridFallback
     * @param {string} details[].pagePath
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_PERSONALIZED_GRID_2', ([{ container, gameFeed, personalizedCategoryName, personalizedGridFallback, pagePath }]) => { ... });
     */
    RESPONSIVE_GAME_PORTLET_PERSONALIZED_GRID_2: 'games:personalizedGrid:2',
    /**
     * The event is fired when search field input is changed.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_SEARCH
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].gameFeed
     * @param {string} details[].searchstring
     * @param {number} details[].container
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_SEARCH', ([{ gameFeed, searchString, container }]) => { ... });
     */
    RESPONSIVE_GAME_PORTLET_SEARCH: 'game:search:change',
    /**
     * The event is fired when a game is added/removed from the favorites.
     * @memberOf Playtech.API.events
     * @event GAME_FAVORITES_HANDLER
     * @type {callback}
     * @param {Object[]} details
     * @param {string} details[].gameCode
     * @param {string} details[].method
     * @param {number} details[].gameFeed
     * @param {number} details[].container
     * @example Playtech.on('GAME_FAVORITES_HANDLER', ([{ gameFeed, method, gameCode, container }]) => { ... });
     */
    GAME_FAVORITES_HANDLER: 'gameFavoritesChange',
    GAME_LAUNCH: 'gameGameLaunch'
};
